// 主（あるじ）Lv。上限は本編クリアまで1000（CONFIG.level.maxBeforeClear）、クリア後 CONFIG.level.max。上限以降のEXPは霊玉に換える（EXP倍率の上乗せ分は除く・1日の上限あり）。
import { CONFIG, configVersion } from './config';
import { stageKey, type Profile, type Resources } from './profile';

export function expToNext(level: number): number {
  const c = CONFIG.level;
  const u = c.unit ?? 1;
  // 旧Lv（上限100のとき）の位置。旧Lv1つ分（主Lv u 個）の真ん中で旧式を引く
  const x = (level + (u - 1) / 2) / u;
  // 終盤のLvは必要量を相対的に軽く（taper：from で1倍 → to で mult 倍の直線。BALANCE.md 17章）
  const t = c.taper;
  const k = t && t.to > t.from ? 1 + (t.mult - 1) * Math.max(0, Math.min(1, (x - t.from) / (t.to - t.from))) : 1;
  const after = c.maxBeforeClear && level >= c.maxBeforeClear ? (c.afterClearNeedMult ?? 1) : 1;
  return Math.max(1, Math.round(((c.needA * Math.pow(x, c.needP) + (c.needC ?? 0) * x + c.needB) * k * valleyMult(x) * after) / u));
}

/** 旧Lv（上限100のとき）に直した段：主Lv unit 個ごとに1つ上がる（解放力・兵糧の最大値に使う） */
export const levelTier = (level: number) => Math.floor((Math.max(1, level) + (CONFIG.level.unit ?? 1) - 1) / (CONFIG.level.unit ?? 1));

/** 中盤の谷をならす倍率（CONFIG.level.valley の折れ線。旧Lv x で引く。空・範囲外は1） */
function valleyMult(x: number): number {
  const pts = CONFIG.level.valley;
  if (!pts?.length) return 1;
  if (x <= pts[0][0]) return pts[0][1];
  for (let i = 1; i < pts.length; i++) {
    const [x0, y0] = pts[i - 1];
    const [x1, y1] = pts[i];
    if (x <= x1) return y0 + ((y1 - y0) * (x - x0)) / Math.max(1e-9, x1 - x0);
  }
  return pts[pts.length - 1][1];
}

let cache = { ver: -1, totals: [] as number[] };
/** totals[L] = Lv L になるまでの累計EXP（totals[1] = 0） */
function totals(): number[] {
  if (cache.ver === configVersion()) return cache.totals;
  const t = [0, 0];
  for (let L = 1; L < CONFIG.level.max; L++) t.push(t[L] + expToNext(L));
  cache = { ver: configVersion(), totals: t };
  return t;
}

/** その人の主Lvの上限：第100陣をクリアした難易度の数（通常から順に）で CONFIG.level.caps を引く */
export function levelCapOf(p: Pick<Profile, 'progress'>): number {
  const caps = CONFIG.level.caps;
  let n = 0;
  while (n < caps.length - 1 && (p.progress[stageKey(100, n)]?.clears ?? 0) > 0) n++;
  return Math.min(CONFIG.level.max, caps[n]);
}

export const expForLevel = (level: number) => totals()[Math.max(1, Math.min(CONFIG.level.max, level))];

/** max：その人の上限（levelCapOf）。省くと全体の上限 */
export function levelInfo(exp: number, max = CONFIG.level.max): { level: number; into: number; need: number; total: number } {
  const t = totals();
  let level = 1;
  // 二分探索
  let lo = 1,
    hi = max;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (t[mid] <= exp) {
      level = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  if (level >= max) return { level: max, into: exp - t[max], need: 0, total: exp };
  return { level, into: exp - t[level], need: t[level + 1] - t[level], total: exp };
}

/**
 * 主EXPを加える。baseExp は倍率を掛ける前の量（freeExp の累計用）、exp は倍率込みの量。
 * 上限Lvを越えた分は捨てる（霊玉への換算は 2026-11-05 にやめた）。Lvアップで兵糧は回復しない（2026-10-17 廃止）。
 */
export function gainExp(profile: Profile, res: Resources, exp: number, baseExp: number, _now: number, _dayKey: string): { levelUps: number } {
  const max = levelCapOf(profile);
  const before = levelInfo(res.exp, max).level;
  res.exp = Math.min(expForLevel(max), res.exp + exp);
  profile.freeExp += baseExp;
  const after = levelInfo(res.exp, max).level;
  profile.level = after;
  return { levelUps: after - before };
}
